// @flow

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import isEmpty from 'lodash/isEmpty';

import I18n from 'PMT/i18n/I18n';

// -- constants
import RoutingEnum from 'constants/RoutingEnum';
import { ViewConstants } from 'constants';
import { ApiConstants } from 'PMT/constants';

// -- utils
import { Utils, CurrencyUtils } from 'PMT/utils';
import { getAllergenLabel } from 'PMT/constants/DietaryConstants';
import SecurityContext from 'utils/permissions/SecurityContext';

// -- entities
import {
  Product,
  RestaurantMenu,
  ProductCategory,
  Menu,
  MenuPart,
} from 'PMT/entities/menu';

import { OrderSettings } from 'PMT/entities/order';

// -- views
import AbstractRestaurantMenuForm from '../components/AbstractRestaurantMenuForm';
import Link from 'PMT/components/Link';
import SvgIcon from 'PMT/components/SvgIcon';
import Switch from 'react-bootstrap-switch';
import { ImageDropzone, FixedSubmitArea } from 'PMT/components/form';
import MenuPartView from './components/MenuPartView';
import MenuCategoryRow from './components/MenuCategoryRow';

class MenuDetailView extends AbstractRestaurantMenuForm {
  static propTypes = {
    menu: PropTypes.instanceOf(Menu).isRequired,

    restaurantMenu: PropTypes.instanceOf(RestaurantMenu).isRequired,

    orderSettings: PropTypes.instanceOf(OrderSettings).isRequired,

    onUpdate: PropTypes.func.isRequired,

    onImageRemove: PropTypes.func,

    currencyCode: PropTypes.string,
  };

  componentWillReceiveProps(nextProps: Object) {
    super.componentWillReceiveProps(nextProps);
  }

  getDefaultState(props: Object) {
    return {
      menu: props.menu.copy(),
      image: null,

      displayEditModal: false,
    };
  }

  shouldUpdateState(nextProps: Object) {
    return !this.props.menu.equals(nextProps.menu);
  }

  onMenuChange = (menu: Menu) => {
    this.setState({
      menu,
    });
  };

  // -- toggle
  onToggleTakeAway = () => {
    const menu: Menu = this.menu;

    menu.prices.takeAway.available = !menu.prices.takeAway.available;

    this.setState({
      menu,
    });
  };

  onToggleDelivery = () => {
    const menu: Menu = this.menu;

    menu.prices.delivery.available = !menu.prices.delivery.available;

    this.setState({
      menu,
    });
  };

  onToggleOnSite = () => {
    const menu: Menu = this.menu;

    menu.prices.onSite.available = !menu.prices.onSite.available;

    this.setState({
      menu,
    });
  };

  //
  // submit
  //

  onSubmit = () => {
    this.props.onUpdate(this.menu, this.state.image);
  };

  onReset = () => {
    this.setState({
      menu: this.props.menu.copy(),
      image: null,
    });
  };

  //
  // image
  //

  onImageChange = (pictureData: string) => {
    this.setState({
      image: pictureData,
    });
  };

  onImageRemove = () => {
    this.props.onImageRemove(this.menu);
  };

  // -- getters

  get menu(): Menu {
    return this.state.menu;
  }

  get restaurantMenu(): RestaurantMenu {
    return this.props.restaurantMenu;
  }

  get haveModifications(): boolean {
    return (
      !this.props.menu.equals(this.state.menu) || !_.isNull(this.state.image)
    );
  }

  // -- renderers

  renderEditButton() {
    if (!SecurityContext.restaurantMenu.canEdit()) {
      return null;
    }

    return (
      <Link
        className="btn btn-primary"
        to={RoutingEnum.RESTAURANT_MENU_MENU_EDIT}
        params={{ menuId: this.menu.id }}
      >
        <i className="fa fa-edit" />
        &nbsp;&nbsp;
        {I18n.tr('menu.menu.detail.edit')}
      </Link>
    );
  }

  renderDescription() {
    if (_.isEmpty(this.menu.description)) {
      return (
        <p>
          <em>{I18n.tr('menu.menu.detail.no_description')}</em>
        </p>
      );
    }

    return <p>{this.menu.description}</p>;
  }

  renderImage() {
    let readOnly =
      SecurityContext.restaurantMenu.canEdit() && false ? false : true;

    return (
      <ImageDropzone
        readOnly={readOnly}
        className="menu_view__image"
        placeholder={ViewConstants.Image.RestaurantMenu.NO_IMAGE_FOR_MENU}
        currentImage={this.state.image}
        image={this.menu.image}
        onChange={this.onImageChange}
        onRemove={this.onImageRemove}
        aspectRatio={ApiConstants.Image.RestaurantMenu.Menu.RATIO}
        width={ApiConstants.Image.RestaurantMenu.Menu.WIDTH}
        height={ApiConstants.Image.RestaurantMenu.Menu.HEIGHT}
        maxSize={ApiConstants.Image.RestaurantMenu.Menu.MAX_SIZE}
      />
    );
  }

  renderPricesTable() {
    const disableModification = !SecurityContext.restaurantMenu.canEdit();

    return (
      <table className="table menu_modal_view__prices_table">
        <thead>
          <tr>
            <td />
            <td>{I18n.tr('menu.menu.detail.table.header.mode')}</td>
            <td className="text-center">
              {I18n.tr('menu.menu.detail.table.header.price')}
            </td>
            <td className="text-center">
              {I18n.tr('menu.menu.detail.table.header.vat')}
            </td>
            <td>{I18n.tr('menu.menu.detail.table.header.activation')}</td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <SvgIcon icon={SvgIcon.ORDER_ON_SITE} />
            </td>
            <td>{I18n.tr('menu.menu.detail.table.header.on_site')}</td>
            <td className="text-center">
              {CurrencyUtils.formatPriceWithCurrency(
                this.menu.prices.onSite.priceIncludingTax,
                  this.props.currencyCode
              )}
            </td>
            <td className="text-center">
              {CurrencyUtils.formatTax(this.menu.prices.onSite.vat)}
            </td>
            <td>
              <Switch
                key={Utils.generateUID()}
                bsSize="mini"
                value={this.menu.prices.onSite.available}
                onChange={this.onToggleOnSite}
                disabled={disableModification}
              />
            </td>
          </tr>
          <tr>
            <td>
              <SvgIcon icon={SvgIcon.ORDER_TAKE_AWAY} />
            </td>
            <td>{I18n.tr('menu.menu.detail.table.header.take_away')}</td>
            <td className="text-center">
              {CurrencyUtils.formatPriceWithCurrency(
                this.menu.prices.takeAway.priceIncludingTax,
                  this.props.currencyCode
              )}
            </td>
            <td className="text-center">
              {CurrencyUtils.formatTax(this.menu.prices.takeAway.vat)}
            </td>
            <td>
              <Switch
                key={Utils.generateUID()}
                bsSize="mini"
                value={this.menu.prices.takeAway.available}
                onChange={this.onToggleTakeAway}
                disabled={disableModification}
              />
            </td>
          </tr>
          <tr>
            <td>
              <SvgIcon icon={SvgIcon.ORDER_DELIVERY} />
            </td>
            <td>{I18n.tr('menu.menu.detail.table.header.delivery')}</td>
            <td className="text-center">
              {CurrencyUtils.formatPriceWithCurrency(
                this.menu.prices.delivery.priceIncludingTax,
                  this.props.currencyCode
              )}
            </td>
            <td className="text-center">
              {CurrencyUtils.formatTax(this.menu.prices.delivery.vat)}
            </td>
            <td>
              <Switch
                key={Utils.generateUID()}
                bsSize="mini"
                value={this.menu.prices.delivery.available}
                onChange={this.onToggleDelivery}
                disabled={disableModification}
              />
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  renderPartsList() {
    const partsListView = this.menu.parts.map((part: MenuPart) => {
      return (
        <MenuPartView
          key={part.id}
          menuPart={part}
          menu={this.menu}
          restaurantMenu={this.restaurantMenu}
          currencyCode={this.props.currencyCode}
        />
      );
    });

    return <div className="row">{partsListView}</div>;
  }

  renderCategoriesList() {
    const categories: Array<ProductCategory> = this.restaurantMenu.getCategoriesWithMenuId(
      this.menu.id
    );

    if (_.isEmpty(categories)) {
      return (
        <div>
          <em>{I18n.tr('menu.menu.detail.no_category_with_product')}</em>
        </div>
      );
    }

    const categoriesListView = [];

    _.each(categories, (category: ProductCategory) => {
      categoriesListView.push(
        <MenuCategoryRow
          key={category.id}
          category={category}
          // onDelete={this.onDeleteMenuFromCategory}
        />
      );
    });

    return (
      <div>
        <ul>{categoriesListView}</ul>
      </div>
    );
  }

  renderSubmitArea() {
    return (
      <FixedSubmitArea
        haveModifications={this.haveModifications}
        onSubmit={this.onSubmit}
        onReset={this.onReset}
      />
    );
  }

  render() {
    try {
      return (
        <div className="menu_view">
          <div className="row">
            <div className="col-xs-12 col-md-4">
              <h3>{this.menu.name}</h3>

              {this.renderDescription()}
            </div>

            <div className="hidden-xs col-md-6">{this.renderImage()}</div>

            <div className="col-sm-2">{this.renderEditButton()}</div>

            <div className="top-buffer-40 col-xs-12 col-md-4">
              {this.menu.dietaryInfo && !isEmpty(this.menu.dietaryInfo) && (
                <span>
                  {I18n.tr('global.allergen.allergens')}:&nbsp;
                  {this.menu.dietaryInfo.allergens
                    .map(getAllergenLabel)
                    .join(', ')}
                </span>
              )}
            </div>
          </div>

          <hr />

          <div className="row">
            <div className="top-buffer-40 col-xs-12 col-md-4">
              <h2>{I18n.tr('menu.menu.detail.price')}</h2>
              {this.renderPricesTable()}
            </div>

            <div className="top-buffer-40 col-xs-12 col-md-4">
              <h2>{I18n.tr('menu.menu.detail.categories')}</h2>
              {this.renderCategoriesList()}
            </div>
          </div>

          <div className="row">
            <div className="top-buffer-40 col-xs-12">
              <h2>{I18n.tr('menu.menu.detail.parts')}</h2>

              {this.renderPartsList()}
            </div>
          </div>

          {this.renderSubmitArea()}
        </div>
      );
    } catch (e) {
      console.error(e);
    }
  }
}

export default MenuDetailView;
